import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  bomKeys,
  getCurrentBom,
  saveBomAdjustment,
  simulateBomAdjustment,
  type BomAdjustment,
  type BomVersion,
} from "@/api/bom";
import { useCurrentUser } from "@/hooks/useAuth";
import { diagnosticKeys } from "@/hooks/useDiagnostic";
import { formatCount } from "@/lib/format";
import { canAdjustBom, hasMinRole } from "@/lib/roles";
import { cn } from "@/lib/utils";

import type { DiagnosticTabProps } from "../DiagnosticSection";
import { formatBrl } from "../diagnosticData";
import { annualList, annualNet } from "./annualNet";
import { formatDiscount as percent } from "./discountPercent";

type Line = BomVersion["lines"][number];

const BUTTON =
  "inline-flex items-center gap-1.5 rounded-md border border-[#d7dde6] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#08254c] hover:bg-[#f5f8fc] disabled:cursor-not-allowed disabled:opacity-50";

function errorDetail(err: unknown): string | null {
  const detail = isAxiosError(err) ? err.response?.data?.detail : undefined;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    if (Array.isArray(detail.errors)) return detail.errors.join(" ");
    if (typeof detail.message === "string") return detail.message;
  }
  return null;
}

function money(value: number | null | undefined): string {
  return value == null ? "—" : formatBrl(value);
}

interface Edit {
  qty: string;
  /** #1511: o desconto em %, como se digita; o ajuste manda a fração. */
  discount?: string;
  justification: string;
  /** RF-08: validar a linha sem mudar a quantidade. */
  status?: "pending" | "validated";
}

type Tab = "all" | "users" | "capacity";

/**
 * RF-07: "Usuários" é a métrica User ou o SKU com perfil de uso; o resto é
 * capacidade e serviços. A linha só do myGROW chega sem métrica, e o perfil de
 * uso é o que diz que ela é de usuário.
 */
function isUserLine(line: Line): boolean {
  return (line.sales_unit_metric ?? "").toLowerCase() === "user" || !!line.usage_profile;
}

/** Desconto em fração para o campo em %, com duas casas (0,1234 → "12.34"). */
function percentText(fraction: number | null | undefined): string {
  return String(Math.round((fraction ?? 0) * 10000) / 100);
}

/** O desconto digitado em %, como fração; `null` fora de 0 a 100% ou vazio (RF-05). */
function parsedDiscount(text: string): number | null {
  if (text.trim() === "") return null;
  const value = Number(text);
  if (!Number.isFinite(value) || value < 0 || value > 100) return null;
  return Math.round(value * 100) / 10000;
}

function signed(value: number): string {
  return `${value > 0 ? "+" : "−"}${formatCount(Math.abs(value))}`;
}

/**
 * Detalhes da BOM vigente, com ajuste (#1345, F4).
 *
 * Na reunião o consultor muda a quantidade de um SKU: o número se refaz pela
 * simulação (que não grava), a justificativa é obrigatória para salvar, e
 * salvar cria uma versão nova que vira a vigente. A versão anterior continua no
 * histórico, intacta. "Descartar" volta à vigente sem chamar nada.
 *
 * O papel `user` vê sem editar. A BOM ainda calculada pelo myGROW não se
 * ajusta: primeiro o arquivo do Pricing App entra pelo nó 06 do Functional.
 *
 * Desde a #1480 ele abre pelo nó da BOM do Functional, e não pelo Ato 5: o preço
 * de lista da SAP não vai para a tela do cliente. As props do Diagnóstico ficam
 * opcionais porque fora dele ninguém as tem.
 *
 * RF-07 e RF-08: abas Usuários / Capacidade e serviços / Todas; quantidade
 * calculada, do Pricing App e vigente, com o Δ contra o Pricing App; e a linha
 * pode ser validada sem mudar a quantidade, o que não pede justificativa.
 *
 * #1511: o desconto também se edita, em %, e refaz o número pela simulação.
 *
 * EF-FUNC-010 (#1621): "Desc. Pricing App" mostra o desconto do arquivo ao lado
 * do aplicado, e a linha que difere dele ganha o selo "Ajustado". Desde a #1714
 * (cartão 6.8) é o desconto total que a SAP cobra na linha, com o geral do deal. Desconto sem
 * justificativa salva com aviso (RF-05); só a quantidade continua pedindo o
 * motivo. Desconto vazio ou fora de 0 a 100% trava o salvar. O desconto em massa
 * (RF-03) preenche o campo das linhas do total, e os cartões (RF-04) somam a
 * versão vigente ou, durante o ajuste, a simulada.
 */
export function BomDetails({
  simulateDelayMs = 300,
}: Partial<DiagnosticTabProps> & { simulateDelayMs?: number }) {
  const { t } = useTranslation();
  const { projectId = "" } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  const role = useCurrentUser()?.role;
  const [edits, setEdits] = useState<Record<string, Edit>>({});
  const [note, setNote] = useState("");
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [tab, setTab] = useState<Tab>("all");
  const [massDiscount, setMassDiscount] = useState("");
  const [onlyBelow, setOnlyBelow] = useState(true);
  const [massStatus, setMassStatus] = useState<string | null>(null);

  const current = useQuery({
    queryKey: bomKeys.current(projectId),
    queryFn: () => getCurrentBom(projectId),
    enabled: !!projectId,
    retry: false,
  });
  const bom = current.data;
  // #1714: validar a linha é de quem alcança `sales` (o consultor inclusive);
  // mudar quantidade e desconto, só do admin e do comercial.
  const canEdit = hasMinRole(role, "sales") && !!bom?.persisted;
  const canAdjust = canEdit && canAdjustBom(role);

  const adjustments: BomAdjustment[] = useMemo(() => {
    if (!bom) return [];
    return bom.lines.flatMap((line): BomAdjustment[] => {
      const edit = edits[line.sku_number];
      if (!edit) return [];
      const qty = edit.qty.trim() === "" ? NaN : Number(edit.qty);
      const qtyChanged = Number.isFinite(qty) && qty >= 0 && qty !== line.qty_current;
      // Vazio ou fora de 0 a 100% não vai: o campo fica vermelho e o salvar, travado.
      const discount = edit.discount == null ? null : parsedDiscount(edit.discount);
      const discountChanged = discount != null && discount !== (line.effective_discount ?? 0);
      if (qtyChanged || discountChanged) {
        return [
          {
            sku_number: line.sku_number,
            qty_current: qtyChanged ? qty : line.qty_current,
            ...(discountChanged ? { discount } : {}),
            validation_status: "adjusted" as const,
            justification: edit.justification.trim() || null,
          },
        ];
      }
      if (edit.status && edit.status !== line.validation_status) {
        return [
          {
            sku_number: line.sku_number,
            qty_current: line.qty_current,
            validation_status: edit.status,
            justification: edit.justification.trim() || null,
          },
        ];
      }
      return [];
    });
  }, [bom, edits]);
  // Só quantidade e desconto mudam o número: validar não passa pela simulação.
  const qtyAdjustments = useMemo(
    () => adjustments.filter((a) => a.validation_status === "adjusted"),
    [adjustments],
  );

  // A simulação espera a digitação parar: uma chamada por pausa, e não por tecla.
  const [debounced, setDebounced] = useState<BomAdjustment[]>([]);
  const simulationKey = JSON.stringify(
    qtyAdjustments.map(({ sku_number, qty_current, discount }) => [
      sku_number,
      qty_current,
      discount ?? null,
    ]),
  );
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(qtyAdjustments), simulateDelayMs);
    return () => clearTimeout(timer);
    // A justificativa não muda o número: só quantidade e desconto disparam a simulação.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulationKey, simulateDelayMs]);

  const simulation = useQuery({
    queryKey: [...bomKeys.current(projectId), "simulate", bom?.version, JSON.stringify(debounced)],
    queryFn: () => simulateBomAdjustment(projectId, bom!.version, debounced),
    enabled: canEdit && debounced.length > 0,
    retry: false,
  });
  const simulated = qtyAdjustments.length > 0 ? simulation.data : undefined;
  const simulatedBySku = useMemo(
    () => new Map((simulated?.lines ?? []).map((line) => [line.sku_number, line])),
    [simulated],
  );

  const save = useMutation({
    mutationFn: () =>
      saveBomAdjustment(projectId, {
        base_version: bom!.version,
        adjustments,
        note: note.trim() || null,
      }),
    onSuccess: (version) => {
      setEdits({});
      setNote("");
      setMessage({
        ok: true,
        text: t("Version {{version}} saved: it is now the current BOM.", { version: version.version }),
      });
      void queryClient.invalidateQueries({ queryKey: bomKeys.all(projectId) });
      void queryClient.invalidateQueries({ queryKey: diagnosticKeys.snapshot(projectId) });
    },
    onError: (err) =>
      setMessage({ ok: false, text: errorDetail(err) ?? t("Could not save the adjustment.") }),
  });

  if (current.isLoading) {
    return <p className="text-[12px] text-[#596b85]">{t("Loading the BOM…")}</p>;
  }
  if (!bom) {
    return <p className="text-[12px] text-[#596b85]">{t("The project has no BOM yet.")}</p>;
  }

  const bySku = new Map(bom.lines.map((line) => [line.sku_number, line]));
  // RF-05: só a quantidade mudada exige o motivo; o desconto sem ele salva com aviso.
  const missingJustification = qtyAdjustments.some(
    (a) => !a.justification && a.qty_current !== bySku.get(a.sku_number)?.qty_current,
  );
  const unjustifiedDiscounts = qtyAdjustments
    .filter(
      (a) =>
        a.discount != null &&
        !a.justification &&
        a.discount !== (bySku.get(a.sku_number)?.discount ?? null),
    )
    .map((a) => a.sku_number);
  const invalidDiscounts = bom.lines
    .filter((line) => {
      const typed = edits[line.sku_number]?.discount;
      return typed != null && parsedDiscount(typed) == null;
    })
    .map((line) => line.sku_number);
  // RF-04: os cartões e a linha de total somam a simulada enquanto há ajuste.
  const totals = simulated?.totals ?? bom.totals;
  const months = bom.contract_period_months;
  const perYear = (value: number) => (months ? (value * 12) / months : value);
  const discountShare = totals.total_list ? totals.total_discount / totals.total_list : 0;
  const simulationError =
    qtyAdjustments.length > 0 && simulation.isError ? errorDetail(simulation.error) : null;
  const statusLabel = (status: string): string =>
    status === "adjusted" ? t("Adjusted") : status === "validated" ? t("Validated") : t("Pending");
  const userLines = bom.lines.filter(isUserLine);
  const capacityLines = bom.lines.filter((line) => !isUserLine(line));
  const shownLines = tab === "users" ? userLines : tab === "capacity" ? capacityLines : bom.lines;
  // RF-07: o perfil de uso de origem é dos SKUs de usuário; a aba de capacidade não tem a coluna.
  const showProfile = tab !== "capacity";
  const reviewed = {
    adjusted: bom.lines.filter((line) => line.validation_status === "adjusted").length,
    validated: bom.lines.filter((line) => line.validation_status === "validated").length,
  };
  const setEdit = (sku: string, patch: Partial<Edit>, line: Line) =>
    setEdits((all) => {
      const base = all[sku] ?? { qty: String(line.qty_current), justification: "" };
      return { ...all, [sku]: { ...base, ...patch } };
    });

  /**
   * RF-03: o desconto mínimo em todas as linhas do total. "Só onde o desconto
   * for menor" não reduz o que já veio maior (os 20% do SAP Finance Base ficam),
   * e a linha sem justificativa recebe a do desconto em massa.
   */
  const applyMassDiscount = () => {
    const fraction = parsedDiscount(massDiscount);
    if (fraction == null) {
      setMassStatus(t("Enter a discount from 0 to 100%."));
      return;
    }
    const reason = t("Minimum discount applied in bulk ({{discount}})", {
      discount: percent(fraction),
    });
    const next = { ...edits };
    let count = 0;
    for (const line of bom.lines) {
      if (!line.in_total) continue;
      const edit = next[line.sku_number] ?? { qty: String(line.qty_current), justification: "" };
      const applied =
        edit.discount == null ? (line.effective_discount ?? 0) : parsedDiscount(edit.discount);
      if (onlyBelow && applied != null && applied >= fraction) continue;
      next[line.sku_number] = {
        ...edit,
        discount: percentText(fraction),
        justification: edit.justification.trim() ? edit.justification : reason,
      };
      count += 1;
    }
    setEdits(next);
    setMassStatus(
      t("Discount of {{discount}} applied to {{count}} line(s).", {
        discount: percent(fraction),
        count,
      }),
    );
  };

  return (
    <div className="space-y-4 text-[12px] text-[#08254c]">
      <p data-testid="bom-details-summary">
        {t("Version {{version}} · {{source}} · TCV {{tcv}} · ACV {{acv}} · {{users}} licensed users", {
          version: bom.version,
          source: bom.persisted
            ? bom.source === "diagnostic_adjustment"
              ? t("adjusted in the Diagnostic")
              : // Nome do produto da SAP: igual nos três idiomas, não passa pela tradução.
                "Pricing App"
            : t("calculated by myGROW"),
          tcv: formatBrl(bom.totals.tcv),
          acv: formatBrl(bom.totals.acv),
          users: formatCount(bom.totals.licensed_users),
        })}
      </p>

      {bom.persisted && (
        <p data-testid="bom-details-review" className="text-[#596b85]">
          {t("Lines adjusted: {{adjusted}} · validated: {{validated}}", reviewed)}
        </p>
      )}

      <div data-testid="bom-details-cards" className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <TotalCard
          testId="bom-card-list"
          label={t("List price / year")}
          value={formatBrl(perYear(totals.total_list))}
          detail={t("List TCV {{value}}", { value: formatBrl(totals.total_list) })}
        />
        <TotalCard
          testId="bom-card-discount"
          label={t("Discount / year")}
          value={formatBrl(perYear(totals.total_discount))}
          detail={t("{{percent}} of the list price (weighted average)", {
            percent: percent(discountShare),
          })}
        />
        <TotalCard
          testId="bom-card-acv"
          highlight
          label={t("ACV with discount")}
          value={formatBrl(totals.acv)}
          detail={t("The SAP GROW software value per year")}
        />
        <TotalCard
          testId="bom-card-tcv"
          highlight
          label={t("TCV with discount")}
          value={formatBrl(totals.tcv)}
          detail={months ? t("{{months}} months", { months: formatCount(months) }) : undefined}
        />
      </div>

      {!bom.persisted && (
        <p
          data-testid="bom-details-not-adjustable"
          className="rounded-md border border-[#e8ebf0] bg-[#f7f9fc] px-3 py-2 text-[#596b85]"
        >
          {t(
            "The current BOM is the one calculated by myGROW. Upload the Pricing App BOM in node 06 of the Functional agent to adjust it here.",
          )}
        </p>
      )}

      {simulated && (
        <div
          data-testid="bom-details-simulated"
          className="rounded-md border border-[#f0d58a] bg-[#fffaeb] px-3 py-2"
        >
          {t("With the adjustments: TCV {{tcv}} ({{delta}}) · ACV {{acv}}", {
            tcv: formatBrl(simulated.totals.tcv),
            delta: `${simulated.totals.tcv >= bom.totals.tcv ? "+" : "−"}${formatBrl(Math.abs(simulated.totals.tcv - bom.totals.tcv))}`,
            acv: formatBrl(simulated.totals.acv),
          })}
          {simulated.adjustment_warnings.length > 0 && (
            <ul className="mt-1 list-disc pl-5 text-[#8a5a00]">
              {simulated.adjustment_warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {simulationError && (
        <p data-testid="bom-details-simulation-error" className="text-[#a23b2f]">
          {simulationError}
        </p>
      )}

      {canAdjust && (
        <div
          data-testid="bom-mass"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-[#d7dde6] bg-[#f7f9fc] px-3 py-2"
        >
          <div className="min-w-[220px] flex-1">
            <p className="font-semibold">{t("Apply a bulk discount")}</p>
            <p className="text-[11px] text-[#596b85]">
              {t("For the minimum discount. Lines out of the total get no discount.")}
            </p>
          </div>
          <label className="inline-flex items-center gap-1">
            {t("Discount")}
            <input
              type="number"
              min={0}
              max={100}
              step={0.01}
              data-testid="bom-mass-discount"
              aria-label={t("Bulk discount (%)")}
              className="w-20 rounded border border-[#d7dde6] px-1.5 py-0.5 text-right"
              value={massDiscount}
              onChange={(event) => setMassDiscount(event.target.value)}
            />
            %
          </label>
          <label className="inline-flex items-center gap-1">
            <input
              type="checkbox"
              data-testid="bom-mass-only-below"
              checked={onlyBelow}
              onChange={(event) => setOnlyBelow(event.target.checked)}
            />
            {t("Only where the discount is lower")}
          </label>
          <button
            type="button"
            data-testid="bom-mass-apply"
            className={BUTTON}
            onClick={applyMassDiscount}
          >
            {t("Apply to lines")}
          </button>
          {massStatus && (
            <span data-testid="bom-mass-status" className="w-full text-[#596b85]">
              {massStatus}
            </span>
          )}
        </div>
      )}

      <div role="tablist" className="flex flex-wrap gap-2">
        {(
          [
            ["all", t("All lines"), bom.lines.length],
            ["users", t("Users"), userLines.length],
            ["capacity", t("Capacity and services"), capacityLines.length],
          ] as const
        ).map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            data-testid={`bom-tab-${key}`}
            onClick={() => setTab(key)}
            className={cn(
              "rounded-full border px-3 py-1 text-[11px] font-semibold",
              tab === key
                ? "border-[#08254c] bg-[#08254c] text-white"
                : "border-[#d7dde6] bg-white text-[#08254c] hover:bg-[#f5f8fc]",
            )}
          >
            {label} <span className="tabular-nums">{count}</span>
          </button>
        ))}
      </div>

      {/* Quadro branco, como as outras telas de detalhe; a tabela rola dentro dele. */}
      <div className="overflow-x-auto rounded-lg border border-[#e8ebf0] bg-white px-3 py-2">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[#e8ebf0] text-left text-[10px] uppercase tracking-wide text-[#7b8799]">
              <th className="py-1 pr-2">{t("SKU")}</th>
              <th className="py-1 pr-2">{t("Description")}</th>
              <th className="py-1 pr-2">{t("Metric")}</th>
              {showProfile && <th className="py-1 pr-2">{t("Usage profile")}</th>}
              <th className="py-1 pr-2 text-right">{t("Calculated qty")}</th>
              <th className="py-1 pr-2 text-right">{t("Pricing App qty")}</th>
              <th className="py-1 pr-2 text-right">{t("Current qty")}</th>
              <th className="py-1 pr-2 text-right">Δ</th>
              <th className="py-1 pr-2 text-right">{t("List price / year")}</th>
              <th
                className="py-1 pr-2 text-right"
                title={t("Total discount the SAP charges on the line, including the deal-wide discount.")}
              >
                {t("Pricing App disc.")}
              </th>
              <th className="py-1 pr-2 text-right">{t("Discount (%)")}</th>
              <th className="py-1 pr-2 text-right">{t("Net in the contract")}</th>
              <th className="py-1 pr-2 text-right">{t("Net / year")}</th>
              <th className="py-1 pr-2">{t("Validation")}</th>
              <th className="py-1">{t("Justification")}</th>
            </tr>
          </thead>
          <tbody>
            {shownLines.map((line) => {
              const edit = edits[line.sku_number];
              const changed = qtyAdjustments.some((a) => a.sku_number === line.sku_number);
              const shownQty = changed
                ? Number(edit?.qty ?? line.qty_current)
                : line.qty_current;
              const reference = line.qty_pricing_app ?? line.qty_calculated;
              const delta = reference == null ? null : shownQty - reference;
              const shown = (changed && simulatedBySku.get(line.sku_number)) || line;
              const editable = canAdjust && line.in_total;
              const typedDiscount = edit?.discount;
              const discountInvalid = typedDiscount != null && parsedDiscount(typedDiscount) == null;
              const appliedDiscount =
                typedDiscount == null ? line.effective_discount : parsedDiscount(typedDiscount);
              // RF-01: o selo compara com o desconto que veio do Pricing App.
              const discountAdjusted =
                line.in_total &&
                line.discount != null &&
                appliedDiscount != null &&
                appliedDiscount !== line.discount;
              return (
                <tr
                  key={line.sku_number}
                  data-testid={`bom-row-${line.sku_number}`}
                  className={cn(
                    "border-b border-[#f2f4f7]",
                    !line.in_total && "text-[#7b8799]",
                    changed && "bg-[#fffaeb]",
                  )}
                >
                  <td className="py-1 pr-2 font-mono">{line.sku_number}</td>
                  <td className="py-1 pr-2">
                    {line.description}
                    {!line.in_total && (
                      <span className="ml-2 text-[11px]">{t("Only in myGROW · not in total")}</span>
                    )}
                  </td>
                  <td className="py-1 pr-2">{line.sales_unit_metric ?? "—"}</td>
                  {showProfile && (
                    <td data-testid={`bom-usage-profile-${line.sku_number}`} className="py-1 pr-2">
                      {line.usage_profile ?? "—"}
                    </td>
                  )}
                  <td
                    data-testid={`bom-qty-calculated-${line.sku_number}`}
                    className="whitespace-nowrap py-1 pr-2 text-right tabular-nums text-[#596b85]"
                  >
                    {line.qty_calculated == null ? "—" : formatCount(line.qty_calculated)}
                  </td>
                  <td
                    data-testid={`bom-qty-pricing-app-${line.sku_number}`}
                    className="whitespace-nowrap py-1 pr-2 text-right tabular-nums text-[#596b85]"
                  >
                    {line.qty_pricing_app == null ? "—" : formatCount(line.qty_pricing_app)}
                  </td>
                  <td className="whitespace-nowrap py-1 pr-2 text-right tabular-nums">
                    {editable ? (
                      <input
                        type="number"
                        min={0}
                        data-testid={`bom-qty-${line.sku_number}`}
                        aria-label={t("Quantity of {{sku}}", { sku: line.sku_number })}
                        className="w-20 rounded border border-[#d7dde6] px-1.5 py-0.5 text-right"
                        value={edit?.qty ?? String(line.qty_current)}
                        onChange={(event) => setEdit(line.sku_number, { qty: event.target.value }, line)}
                      />
                    ) : (
                      formatCount(line.qty_current)
                    )}
                  </td>
                  <td
                    data-testid={`bom-delta-${line.sku_number}`}
                    className="whitespace-nowrap py-1 pr-2 text-right tabular-nums"
                  >
                    {delta ? signed(delta) : ""}
                  </td>
                  <td className="whitespace-nowrap py-1 pr-2 text-right tabular-nums">{money(shown.list_price_annual)}</td>
                  <td
                    data-testid={`bom-discount-pricing-app-${line.sku_number}`}
                    className="whitespace-nowrap py-1 pr-2 text-right tabular-nums text-[#596b85]"
                  >
                    {percent(line.discount)}
                  </td>
                  <td className="whitespace-nowrap py-1 pr-2 text-right tabular-nums">
                    {editable ? (
                      <span className="inline-flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={0.01}
                          data-testid={`bom-discount-${line.sku_number}`}
                          aria-label={t("Discount of {{sku}}", { sku: line.sku_number })}
                          aria-invalid={discountInvalid}
                          className={cn(
                            "w-20 rounded border px-1.5 py-0.5 text-right",
                            discountInvalid ? "border-[#a23b2f] bg-[#fdecea]" : "border-[#d7dde6]",
                          )}
                          value={typedDiscount ?? percentText(line.effective_discount)}
                          onChange={(event) =>
                            setEdit(line.sku_number, { discount: event.target.value }, line)
                          }
                        />
                        %
                      </span>
                    ) : (
                      <span data-testid={`bom-discount-value-${line.sku_number}`}>
                        {percent(shown.effective_discount)}
                      </span>
                    )}
                    {discountAdjusted && (
                      <div>
                        <span
                          data-testid={`bom-discount-adjusted-${line.sku_number}`}
                          className="mt-0.5 inline-block rounded-full bg-[#e8f0fb] px-1.5 text-[10px] font-semibold text-[#1d4f91]"
                        >
                          {t("Adjusted")}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap py-1 pr-2 text-right tabular-nums">{money(shown.net_contract_value)}</td>
                  <td className="whitespace-nowrap py-1 pr-2 text-right tabular-nums">
                    {money(annualNet(shown))}
                    {/* RF-02: na linha ajustada, a lista riscada abaixo do líquido. */}
                    {discountAdjusted && annualList(shown) != null && (
                      <div className="text-[11px] text-[#7b8799] line-through">
                        {money(annualList(shown))}
                      </div>
                    )}
                  </td>
                  <td className="py-1 pr-2">
                    {changed ? (
                      statusLabel("adjusted")
                    ) : canEdit && line.in_total && line.validation_status !== "adjusted" ? (
                      <select
                        data-testid={`bom-status-${line.sku_number}`}
                        aria-label={t("Validation")}
                        value={edit?.status ?? line.validation_status}
                        onChange={(event) =>
                          setEdit(
                            line.sku_number,
                            { status: event.target.value as "pending" | "validated" },
                            line,
                          )
                        }
                        className="rounded border border-[#d7dde6] bg-white px-1.5 py-0.5"
                      >
                        <option value="pending">{statusLabel("pending")}</option>
                        <option value="validated">{statusLabel("validated")}</option>
                      </select>
                    ) : (
                      statusLabel(line.validation_status)
                    )}
                  </td>
                  <td className="py-1">
                    {changed ? (
                      <input
                        type="text"
                        data-testid={`bom-justification-${line.sku_number}`}
                        placeholder={t("Why does this line change?")}
                        className="w-56 rounded border border-[#d7dde6] px-1.5 py-0.5"
                        value={edit?.justification ?? ""}
                        onChange={(event) =>
                          setEdit(line.sku_number, { justification: event.target.value }, line)
                        }
                      />
                    ) : (
                      line.justification ?? ""
                    )}
                  </td>
                </tr>
              );
            })}
            <tr
              data-testid="bom-total-row"
              className="border-t-2 border-[#e8ebf0] bg-[#f7f9fc] font-semibold"
            >
              <td className="py-1 pr-2" colSpan={showProfile ? 8 : 7}>
                {t("Total (BOM lines)")}
              </td>
              <td className="whitespace-nowrap py-1 pr-2 text-right tabular-nums">{formatBrl(perYear(totals.total_list))}</td>
              <td />
              <td className="whitespace-nowrap py-1 pr-2 text-right tabular-nums">{percent(discountShare)}</td>
              <td className="whitespace-nowrap py-1 pr-2 text-right tabular-nums">{formatBrl(totals.tcv)}</td>
              <td className="whitespace-nowrap py-1 pr-2 text-right tabular-nums">{formatBrl(totals.acv)}</td>
              <td colSpan={2} />
            </tr>
          </tbody>
        </table>
      </div>

      {canAdjust && invalidDiscounts.length > 0 && (
        <p
          data-testid="bom-details-invalid-discount"
          className="rounded-md bg-[#fdecea] px-3 py-2 text-[#a23b2f]"
        >
          {t("Invalid discount in {{skus}}: use 0 to 100%. Fix it to save.", {
            skus: invalidDiscounts.join(", "),
          })}
        </p>
      )}

      {canAdjust && unjustifiedDiscounts.length > 0 && (
        <p
          data-testid="bom-details-unjustified-discount"
          className="rounded-md bg-[#fffaeb] px-3 py-2 text-[#8a5a00]"
        >
          {t("Discount different from the Pricing App without a justification: {{skus}}.", {
            skus: unjustifiedDiscounts.join(", "),
          })}
        </p>
      )}

      {canEdit && (
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            data-testid="bom-details-note"
            placeholder={t("Note for this version (optional)")}
            className="w-72 rounded border border-[#d7dde6] px-2 py-1"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <button
            type="button"
            data-testid="bom-details-discard"
            className={BUTTON}
            disabled={adjustments.length === 0 && !note}
            onClick={() => {
              setEdits({});
              setNote("");
              setMessage(null);
              setMassStatus(null);
            }}
          >
            {t("Discard")}
          </button>
          <button
            type="button"
            data-testid="bom-details-save"
            className={BUTTON}
            disabled={
              adjustments.length === 0 ||
              missingJustification ||
              invalidDiscounts.length > 0 ||
              !!simulationError ||
              save.isPending
            }
            onClick={() => save.mutate()}
          >
            {t("Save as a new version")}
          </button>
          {adjustments.length > 0 && missingJustification && (
            <span className="text-[#8a5a00]">
              {t("Each line with a changed quantity needs a justification.")}
            </span>
          )}
        </div>
      )}

      {message && (
        <p
          data-testid="bom-details-status"
          className={message.ok ? "text-[#2e6b45]" : "text-[#a23b2f]"}
        >
          {message.text}
        </p>
      )}
    </div>
  );
}

function TotalCard({
  testId,
  label,
  value,
  detail,
  highlight,
}: {
  testId: string;
  label: string;
  value: string;
  detail?: string;
  /** ACV e TCV com desconto: os números que vão para o Investimento. */
  highlight?: boolean;
}) {
  return (
    <div
      data-testid={testId}
      className={cn(
        "rounded-lg border px-3 py-2",
        highlight ? "border-[#ffc83d] bg-[#fffaeb]" : "border-[#e8ebf0] bg-white",
      )}
    >
      <p className="text-[10px] uppercase tracking-wide text-[#7b8799]">{label}</p>
      <p className="text-[18px] font-semibold tabular-nums">{value}</p>
      {detail && <p className="text-[11px] text-[#596b85]">{detail}</p>}
    </div>
  );
}
